import { ApprovalStatus, OfferApprovalType, OfferStage } from '@/features/offers/constants'
import { offerActionGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerActionEditRights'
import { offerBookingGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerBookingEditRights'
import { getStage } from '@/features/offers/helpers'
import { formatOffer } from '@/features/offers/helpers/formatOffer'
import { authSelector } from '@/stores/auth/authSlice'
import { setApprovalInvitedUsers, showApprovalRemarkModal, showResubmitOfferModal } from '@/stores/offers/offersSlice'
import { useConfirmationModalManagement } from '@/stores/ui/confirmationModalSlice'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { showModal } from '@/stores/ui/modalSlice'
import { findWithIndex } from '@/utils/arrayHelper'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import { Button } from '@chakra-ui/react'
import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import { offerGCPRGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerGCPREditRight'
import useOffer from '@/features/offers/hooks/useOffer'
import useOfferApproval from '@/features/offers/hooks/useOfferApproval'
import ApproverSlotActionButton from '@/features/offers/offer-detail/components/ApproverSlotActionButton'
import { findApproverSlots, findInviteeSlots } from '@/features/offers/offer-detail/helper/approverSlotMatching.helper'
import { APPROVAL_TABLE_TYPE } from '@/features/offers/offer-detail/helper/approverTableColumn.helper'
import {
  selectIsOfferFreeze,
  selectOfferDetail,
  selectOfferPermissions,
} from '@/features/offers/slices/offerDetail.slice'
import { collectDraftGCPRSubmissions, collectDraftPRSubmissions } from '@eapproval/offer-shared'

const BookingButtonGroup = ({ tabIndex }) => {
  const offerPermissions = useSelector(selectOfferPermissions)
  const { adminMode } = useSelector(selectOfferDetail)
  const isOfferFreeze = useSelector(selectIsOfferFreeze)
  const { user: currentUser } = useSelector(authSelector)
  const dispatch = useDispatch()
  const { getValues, formState } = useFormContext()
  const { open: openConfirmationModal } = useConfirmationModalManagement()

  const { useUpdateOffer, useSaveOfferBooking } = useOffer()
  const { useSubmitOfferBooking } = useOfferApproval()
  const updateOffer = useUpdateOffer()
  const submitOfferBooking = useSubmitOfferBooking()
  const saveOfferBooking = useSaveOfferBooking()

  const status = useWatch({ name: 'status' })

  const canSaveBooking =
    getStage(status) === OfferStage.BOOKING &&
    isOfferFreeze &&
    (offerPermissions[offerBookingGroupPolicyType.BOOKING_WBS_NUMBER]?.edit ||
      offerPermissions[offerBookingGroupPolicyType.BOOKING_SO_NUMBER]?.edit ||
      offerPermissions[offerBookingGroupPolicyType.BOOKING_CUSTOMER_PO]?.edit ||
      offerPermissions[offerGCPRGroupPolicyType.GCPR_PR_SUBMISSION]?.edit)

  const canSubmitBooking = offerPermissions[offerActionGroupPolicyType.ACTION_SUBMIT_BOOKING]?.edit ?? false
  const canResubmitBooking = offerPermissions[offerActionGroupPolicyType.ACTION_RESUBMIT_BOOKING]?.edit ?? false
  const canDoBookingApproval = offerPermissions[offerActionGroupPolicyType.ACTION_BOOKING_APPROVAL]?.edit ?? false
  const canDoBookingApprovalInvitation =
    offerPermissions[offerActionGroupPolicyType.ACTION_BOOKING_APPROVAL_INVITATION]?.edit ?? false
  const canReplyBookingInvitation =
    offerPermissions[offerActionGroupPolicyType.ACTION_REPLY_BOOKING_INVITATION]?.edit ?? false

  const bookingApproverDetails = useWatch({ name: 'booking.approverDetails' })

  const inviteSlots = useMemo(
    () => findApproverSlots(bookingApproverDetails, currentUser?._id, [ApprovalStatus.REQUIRED]),
    [bookingApproverDetails, currentUser?._id]
  )
  const replySlots = useMemo(
    () => findInviteeSlots(bookingApproverDetails, currentUser?._id),
    [bookingApproverDetails, currentUser?._id]
  )

  const handleSaveBooking = async () => {
    try {
      const id = getValues('_id')
      const formattedOffer = formatOffer(getValues())

      const updateInput = {
        subSystems: (formattedOffer?.subSystems || []).map((subSystem) => ({
          bookingWBSNumber: subSystem?.bookingWBSNumber || '',
        })),
        SONumber: getValues('booking.SONumber'),
        customerPONumber: getValues('booking.customerPONumber'),
        PODate: getValues('booking.PODate'),
        projectExecutionGCMaterialDetails: formattedOffer?.sourcing?.projectExecutionGCMaterialDetails,
        GCMaterialSubmissions: formattedOffer?.sourcing?.GCMaterialSubmissions,
        costBudgets: formattedOffer?.sourcing?.costBudgets,
      }

      dispatch(showLoadingOverlay())
      await saveOfferBooking.mutateAsync({ offerId: id, data: updateInput })
    } catch ({ response: { data: errorResponseObj } }) {
      //TODO: Add back error handle for switch tab
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleSubmitBooking = async () => {
    try {
      const offerId = getValues('_id')
      const offer = getValues()
      const formattedOffer = formatOffer(offer)
      dispatch(showLoadingOverlay())
      const updateResult = await updateOffer.mutateAsync({ id: offerId, data: formattedOffer })

      const savedSourcing = updateResult?.data?.sourcing ?? formattedOffer?.sourcing ?? {}

      const draftSubmissions = [
        ...collectDraftGCPRSubmissions(savedSourcing.GCMaterialSubmissions),
        ...collectDraftPRSubmissions(savedSourcing.costBudgets),
      ]

      if (draftSubmissions.length > 0) {
        dispatch(
          showModal({
            modalType: MODAL_TYPES.DRAFT_PR_SUBMISSION,
            modalProps: { draftSubmissions, offerId: offerId },
          })
        )
      } else {
        await submitOfferBooking.mutateAsync({ id: offerId })
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleResubmitBooking = () => {
    const currentOffer = getValues()
    const formattedOffer = formatOffer(currentOffer)
    dispatch(
      showResubmitOfferModal({
        offerStage: 'booking',
        offerId: currentOffer.id,
        offerDetail: formattedOffer,
      })
    )
  }

  const handleBookingApprovalModal = async (approvalType) => {
    const currentOffer = getValues()
    const { isDirty, dirtyFields, touchedFields } = formState
    if (isDirty || Object.keys(dirtyFields).length > 0 || Object.keys(touchedFields).length > 0) {
      const { isConfirmed } = await openConfirmationModal({
        title: 'Confirm changes to the offer',
        message: "You've modified the offer, are you sure to save the changes to proceed ? ",
      })
      if (!isConfirmed) return
      try {
        dispatch(showLoadingOverlay())
        const formattedOffer = formatOffer(currentOffer)
        await updateOffer.mutateAsync({ id: currentOffer._id, data: formattedOffer })
      } catch (error) {
        return
      } finally {
        dispatch(hideLoadingOverlay())
      }
    }

    const { index, object: approverDetail } = findWithIndex(
      currentOffer.booking.approverDetails,
      //same user can have various approverDetails status with different level
      (detail) =>
        (detail?.user?._id === currentUser._id || detail?.delegationDetail?.delegatee === currentUser._id) &&
        detail?.status === ApprovalStatus.REQUIRED
    )

    dispatch(
      showApprovalRemarkModal({
        approvalType,
        offerId: currentOffer._id,
        remark: approverDetail?.status === ApprovalStatus.REQUIRED ? approverDetail?.remark : '',
        updatePath: `booking.approverDetails.${index}`,
        approverType: approverDetail?.type,
        approverLevel: approverDetail?.level,
      })
    )
  }

  const handleBookingReplyModal = async (slot) => {
    const currentOffer = getValues()
    const { isDirty, dirtyFields, touchedFields } = formState
    if (isDirty || Object.keys(dirtyFields).length > 0 || Object.keys(touchedFields).length > 0) {
      const { isConfirmed } = await openConfirmationModal({
        title: 'Confirm changes to the offer',
        message: "You've modified the offer, are you sure to save the changes to proceed ? ",
      })
      if (!isConfirmed) return
      try {
        dispatch(showLoadingOverlay())
        const formattedOffer = formatOffer(currentOffer)
        await updateOffer.mutateAsync({ id: currentOffer._id, data: formattedOffer })
      } catch (error) {
        return
      } finally {
        dispatch(hideLoadingOverlay())
      }
    }

    const { approverIndex, detail: inviterDetail } = slot

    dispatch(setApprovalInvitedUsers(inviterDetail?.invitedUsers || []))
    dispatch(
      showModal({
        modalType: MODAL_TYPES.INVITATION_DETAIL,
        modalProps: {
          updateInvitationParameter: {
            offerId: currentOffer._id,
            approverIndex,
          },
          approvalRole: inviterDetail?.type,
          canManageInvitations: canDoBookingApprovalInvitation,
          tableType: APPROVAL_TABLE_TYPE.BOOKING_APPROVAL,
        },
      })
    )
  }

  const handleOpenBookingInvitationDetailModal = async (slot) => {
    const currentOffer = getValues()
    const { isDirty, dirtyFields, touchedFields } = formState
    if (isDirty || Object.keys(dirtyFields).length > 0 || Object.keys(touchedFields).length > 0) {
      const { isConfirmed } = await openConfirmationModal({
        title: 'Confirm changes to the offer',
        message: "You've modified the offer, are you sure to save the changes to proceed ? ",
      })
      if (!isConfirmed) return
      try {
        dispatch(showLoadingOverlay())
        const formattedOffer = formatOffer(currentOffer)
        await updateOffer.mutateAsync({ id: currentOffer._id, data: formattedOffer })
      } catch (error) {
        return
      } finally {
        dispatch(hideLoadingOverlay())
      }
    }

    const { approverIndex, detail: approverDetail } = slot

    dispatch(setApprovalInvitedUsers(approverDetail?.invitedUsers || []))
    dispatch(
      showModal({
        modalType: MODAL_TYPES.INVITATION_DETAIL,
        modalProps: {
          updateInvitationParameter: {
            offerId: currentOffer._id,
            approverIndex,
          },
          approvalRole: approverDetail?.type,
          canManageInvitations: canDoBookingApprovalInvitation,
          tableType: APPROVAL_TABLE_TYPE.BOOKING_APPROVAL,
        },
      })
    )
  }

  return (
    <>
      {canSaveBooking && !adminMode && (
        <Button colorScheme="blue" onClick={handleSaveBooking}>
          Save Booking
        </Button>
      )}
      {canDoBookingApproval && !adminMode && ![7, 8].includes(tabIndex) && (
        <>
          <Button colorScheme="green" onClick={() => handleBookingApprovalModal(OfferApprovalType.BOOKING_APPROVE)}>
            Approve
          </Button>
          <Button colorScheme="red" onClick={() => handleBookingApprovalModal(OfferApprovalType.BOOKING_REJECT)}>
            Reject
          </Button>
          {canDoBookingApprovalInvitation && (
            <ApproverSlotActionButton
              label="Invite"
              colorScheme="teal"
              slots={inviteSlots}
              onSelect={handleOpenBookingInvitationDetailModal}
            />
          )}
        </>
      )}

      {canReplyBookingInvitation && !adminMode && ![7, 8].includes(tabIndex) && (
        <ApproverSlotActionButton
          label="Reply Booking"
          colorScheme="green"
          slots={replySlots}
          onSelect={handleBookingReplyModal}
        />
      )}
      {canSubmitBooking && !adminMode && ![7, 8].includes(tabIndex) && (
        <Button colorScheme="green" onClick={handleSubmitBooking}>
          Submit Booking
        </Button>
      )}
      {canResubmitBooking && !adminMode && ![7, 8].includes(tabIndex) && (
        <Button colorScheme="green" onClick={handleResubmitBooking}>
          Resubmit Booking
        </Button>
      )}
    </>
  )
}

export default BookingButtonGroup
